import { Type } from "@sinclair/typebox";
import "./formats.js";
import { Decision } from "../decision.js";
import { QUESTION_IDS } from "../question.js";
import { REASON_CODE_REGEX } from "../reason-code.js";
/**
 * One schema per endpoint yields request validation, TypeScript types and the
 * OpenAPI document from a single definition. Schema validation is load-bearing
 * here, not decorative: registry payloads are irregular and must be rejected at
 * the boundary rather than deep in the rules.
 *
 * **The `description`s below are the integration guide.** A JSDoc comment on a
 * field never reaches the OpenAPI document; only the schema's own `description`
 * does, and that document — served at `/v1/openapi.json`, browsed at
 * `/v1/docs` — is what a consumer integrates against. So the sentence a
 * consumer needs lives on the field, in English like the rest of the contract.
 */
/** Minor units and an ISO 4217 code. No floats anywhere near money. */
export const MoneySchema = Type.Object({
    amount: Type.Integer({
        minimum: 0,
        // The audit record stores this in a 32-bit integer column; without the
        // bound a larger value passed validation and the decision's own insert
        // failed — an answered decision nothing recorded.
        maximum: 2_147_483_647,
        description: "Minor units — 12660 is 126.60 PLN. Never a float. At most 2147483647 (21.4M PLN); a larger amount is a 400, not a decision.",
        examples: [12660],
    }),
    currency: Type.String({
        pattern: "^[A-Z]{3}$",
        description: "ISO 4217 code.",
        examples: ["PLN"],
    }),
}, { additionalProperties: false, description: "An amount of money in minor units." });
/**
 * Which question is being asked. Validated as an enumeration of what this build
 * answers, so an unknown question is a `400` at the boundary rather than a
 * decision answered from an empty rule set.
 */
export const QuestionSchema = Type.Union(QUESTION_IDS.map((questionId) => Type.Literal(questionId)), {
    description: "The question asked about the entity. Today the only one is `deferred-payment`: can this customer pay later, right now? An unknown value is a 400, not a decision.",
    examples: [QUESTION_IDS[0]],
});
/**
 * A **closed set of SAURON-defined fields**, never a free-form bag: no
 * apiClient's schema may leak into ours. A question reads the fields it needs
 * and ignores the rest; adding a field for a new question is not a breaking
 * change.
 */
export const DecisionContextSchema = Type.Object({
    orderReference: Type.Optional(Type.String({
        minLength: 1,
        maxLength: 128,
        description: "Your own reference for the order, kept on the decision record so support can find it. Not interpreted.",
        examples: ["WL-2026-08-28-0042"],
    })),
    cartValue: Type.Optional(MoneySchema),
}, {
    additionalProperties: false,
    description: "Optional facts about the order. A closed set: unknown fields are rejected. **A rule may decline to run when a field is missing**, so omitting one is a decision and not a saving: a rule configured to ask only above a cart value does not ask at all when no cart value is sent, and the answer comes back without it having been consulted. Send what you have.",
});
export const DecisionRequestSchema = Type.Object({
    /**
     * VAT identifier, with or without a country prefix. A bare number is taken
     * as Polish. Normalised and validated at the boundary; the country is kept
     * rather than stripped, so the key stays unambiguous now that the system
     * can serve more than one country.
     */
    taxId: Type.String({
        minLength: 4,
        maxLength: 20,
        description: "The entity's VAT identifier with its country prefix (PL5252445767, DE123456789, ATU12345678); a bare number is read as a Polish NIP. Spaces, dots and dashes are ignored. A Polish number with a failed checksum, or an EU number that does not have the structure its member state issues, is a 400. Greece is EL and Northern Ireland XI, as printed on invoices. For a sole trader this is personal data: send it in the body, never in a URL or a log line.",
        examples: ["PL5252445767", "DE123456789"],
    }),
    /** Required: a decision is the answer to a named question. */
    question: QuestionSchema,
    /** Caller-generated. The idempotency key. */
    requestId: Type.String({
        format: "uuid",
        description: "Caller-generated UUID, unique per order. The idempotency key: the same requestId returns the same stored decision forever and never re-evaluates, so a retry after a timeout is always safe. Reusing it for another taxId or another question is a 400.",
        examples: ["9f1c9d0a-6f0e-4d2f-9a3b-4b7f1c2d3e4f"],
    }),
    context: Type.Optional(DecisionContextSchema),
}, {
    additionalProperties: false,
    $id: "DecisionRequest",
    description: "One question about one entity.",
});
export const ReasonSchema = Type.Object({
    /**
     * `namespace.code`, validated by shape rather than enumerated. Providers own
     * their own namespaces, so adding one must not change this document —
     * apiClients already have to tolerate an unknown code by falling back to
     * `allowDeferred`.
     */
    code: Type.String({
        pattern: REASON_CODE_REGEX,
        description: "A stable, namespaced code such as `mf.vat_removed` or `core.provider_unavailable`. The namespace is the source that produced it. New codes appear without notice — treat an unknown one as informational and branch on `allowDeferred`.",
        examples: ["mf.vat_removed"],
    }),
    /** Null for outcomes no rule produced, such as a manual override. */
    rule: Type.Union([Type.String(), Type.Null()], {
        description: "The rule that recorded it, or null for an outcome no rule produced (a manual override).",
        examples: ["vat-removed"],
    }),
    kind: Type.Union([
        Type.Literal("VETO"),
        Type.Literal("SCORE"),
        Type.Literal("UNAVAILABLE"),
        Type.Literal("OVERRIDE"),
    ], {
        description: "VETO blocked on its own. SCORE contributed points. UNAVAILABLE means the source could not answer and the rule's fail-closed policy applied. OVERRIDE means a person substituted the answer.",
    }),
    /** Minimised: never a name or an address — they play no part in the decision. */
    observed: Type.Optional(Type.Record(Type.String(), Type.Unknown(), {
        description: "What the rule actually saw, minimised to the fields the decision read — for the VAT rule its status and dates. Never a name, an address or an account number.",
        examples: [{ statusVat: "Niezarejestrowany", removalDate: "2025-06-30" }],
    })),
}, { additionalProperties: false, description: "One finding, in the order it was evaluated." });
export const DecisionResponseSchema = Type.Object({
    decisionId: Type.String({
        format: "uuid",
        description: "Ours. Quote it in a support ticket; `GET /v1/decisions/{decisionId}` returns the same record.",
    }),
    /** The question this answers. Today always `deferred-payment`. */
    question: QuestionSchema,
    decision: Type.Union(Object.values(Decision).map((value) => Type.Literal(value)), {
        description: "ALLOW — deferred payment granted. PREPAYMENT — we still sell, the customer pays upfront; never a refusal. REVIEW is reserved and not returned today. An unknown value must be read as `allowDeferred: false`.",
        examples: [Decision.PREPAYMENT],
    }),
    /** Always present and always consistent with `decision`. */
    allowDeferred: Type.Boolean({
        description: "The one field to branch on. True only for ALLOW, so every decision value added later falls to prepayment by construction.",
    }),
    /** True when fail-closed produced this rather than complete data. */
    degraded: Type.Boolean({
        description: "True when the answer came from fail-closed — a register could not be reached or answered in time — rather than from complete data. The decision is binding either way; the flag tells a prepayment caused by an outage from one caused by a finding.",
    }),
    reasons: Type.Array(ReasonSchema, {
        description: "Why, in evaluation order. Empty for an unremarkable ALLOW. Explains the answer; does not change it.",
    }),
    evaluatedAt: Type.String({ format: "date-time", description: "When the rules ran, UTC." }),
    configVersion: Type.Integer({
        minimum: 0,
        description: "Which published rule configuration produced this. Version 0 is the built-in default before anything was published.",
    }),
}, {
    additionalProperties: false,
    $id: "DecisionResponse",
    description: "The answer, its reasons, and enough to defend it later. A retry with the same requestId returns this byte for byte.",
});
/**
 * Errors are for things the caller did wrong or a genuine defect. **Every
 * foreseeable failure answers 200 with PREPAYMENT and `degraded`** instead —
 * a 5xx would move fail-closed into somebody else's retry handler.
 */
export const ErrorResponseSchema = Type.Object({
    error: Type.String({
        description: "A stable, machine-readable code: `invalid_request`, `core.nip_invalid`, `request_id_reused`, `unauthorized`, `rate_limited`, `not_found`, `internal_error`.",
        examples: ["invalid_request"],
    }),
    message: Type.String({ description: "For a person reading a log. Not stable; do not parse." }),
}, {
    additionalProperties: false,
    $id: "ErrorResponse",
    description: "Something the caller did wrong, or a defect. No decision was reached, so nothing was recorded — and the caller must treat the outcome as prepayment.",
});
//# sourceMappingURL=schemas.js.map